import { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import PortalPopover from '../PortalPopover';
import DistIcon from './DistIcon';
import { parseNum, displayNum } from './numFormat';
import { useToleranceStore } from '../../../stores/toleranceStore';
import { CPK_SCHNELLWAHL, normCpk, cpkText } from '../../../utils/cpk';

// Per-row distribution picker. Pro-only at the row level (the column
// header hides the column entirely for Free users — see MeasureTable).
// Distributions follow the canonical store names: normal | uniform |
// triangular. Each option carries a mini-glyph (DistIcon) that visualizes
// the shape — bell / box / triangle — so the active distribution is
// glanceable without reading the chip text.

// Jede Option trägt eine knappe Entscheidungshilfe (when + ex), die im
// Popover unter dem Label erscheint — damit die Wahl ohne Norm-Nachschlagen
// gelingt. Ausführliche Erklärung mit mehr Beispielen: FAQ-Eintrag
// „Welche Verteilung wähle ich…".
const OPTS = [
  { id: 'normal',     label: 'Normal',  sub: 'σ = T/(6·Cpk)', when: 'Serienfertigung, stabiler Prozess', ex: 'gedrehte Welle in Serie' },
  { id: 'uniform',    label: 'Gleich',  sub: '[Min, Max]',    when: 'kein Prozesswissen / driftend',     ex: 'Zukaufteil, Lagespiel' },
  { id: 'triangular', label: 'Dreieck', sub: 'Peak = Nenn',   when: 'manuelle Arbeit, Kleinserie',       ex: 'Handmontage auf Mitte' },
];

function labelFor(id) {
  return OPTS.find((o) => o.id === id) ?? OPTS[0];
}

// Cpk 1 und „kein Cpk“ rechnen gleich. Gespeichert wird dann null, damit
// Maße ohne Fähigkeitsangabe in Dateien unverändert bleiben.
function speicherwert(c) {
  return c === 1 ? null : c;
}

// Cpk-Eingabe (C+17), nur bei Normal. Jeder gültige Wert wird sofort
// übernommen, ungültige Eingaben zeigen einen Feldfehler und ändern nichts.
function CpkFeld({ cpk, onUpdate }) {
  const mcAktiv = useToleranceStore((s) => s.mode === 'montecarlo');
  const [entwurf, setEntwurf] = useState(() => {
    const c = normCpk(cpk);
    return c === null || c === 1 ? '' : displayNum(c);
  });
  const [fehler, setFehler] = useState(false);
  const wert = normCpk(cpk) ?? 1;

  const eingabe = (s) => {
    setEntwurf(s);
    if (s.trim() === '') { setFehler(false); onUpdate({ cpk: null }); return; }
    const c = normCpk(parseNum(s));
    if (c === null) { setFehler(true); return; }
    setFehler(false);
    onUpdate({ cpk: speicherwert(c) });
  };
  const waehle = (c) => {
    setEntwurf(c === 1 ? '' : displayNum(c));
    setFehler(false);
    onUpdate({ cpk: speicherwert(c) });
  };

  return (
    <div className="border-t border-line" style={{ padding: '8px 10px 6px', fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span className="text-ink-2" style={{ fontWeight: 600 }}>Cpk</span>
        <input
          type="text"
          inputMode="decimal"
          aria-label="Cpk"
          aria-invalid={fehler}
          placeholder="1,0"
          value={entwurf}
          onChange={(e) => eingabe(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className={`num bg-surface text-ink border ${fehler ? 'border-err' : 'border-line'}`}
          style={{ width: 52, height: 22, borderRadius: 4, padding: '0 6px', fontSize: 11 }}
        />
        {CPK_SCHNELLWAHL.map((c) => (
          <button
            key={c}
            type="button"
            aria-pressed={wert === c}
            onClick={() => waehle(c)}
            className={wert === c ? 'bg-brand-soft text-brand font-semibold' : 'bg-surface-2 text-ink-2 hover:text-ink'}
            style={{ border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 10.5, cursor: 'pointer' }}
          >
            {cpkText(c)}
          </button>
        ))}
      </div>
      {fehler && (
        <div role="alert" className="text-err" style={{ marginTop: 4 }}>Cpk zwischen 0,5 und 3,0</div>
      )}
      {!fehler && wert < 1 && (
        <div className="text-warn" style={{ marginTop: 4 }}>Cpk unter 1: Teile außerhalb der Toleranz</div>
      )}
      {!mcAktiv && (
        <div className="text-ink-4" style={{ marginTop: 4 }}>Wirkt nur in Monte Carlo</div>
      )}
    </div>
  );
}

export default function DistChip({ value, cpk, onUpdate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = labelFor(value);
  const c = normCpk(cpk);
  const zeigeCpk = current.id === 'normal' && c !== null && c !== 1;
  // Kompakt „Cpk 1,33“: die Glocke zeigt schon „Normal“, und die Spalte ist
  // nur 120 px breit. Der volle Text steht im Tooltip.
  const text = zeigeCpk ? `Cpk ${cpkText(c)}` : current.label;
  const tooltip = zeigeCpk ? `${current.label}, Cpk ${cpkText(c)}` : current.label;

  return (
    <>
      <button
        ref={ref}
        type="button"
        tabIndex={-1}
        onClick={() => setOpen((o) => !o)}
        title={`Verteilung: ${tooltip} (${current.sub})`}
        className="bg-surface-2 border border-line text-ink-2 hover:text-ink"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '0 8px', height: 24,
          borderRadius: 5, cursor: 'pointer',
          fontSize: 11, fontWeight: 600,
        }}
      >
        <DistIcon kind={current.id} size={14} />
        <span>{text}</span>
        <ChevronDown size={11} aria-hidden="true" />
      </button>
      <PortalPopover anchorRef={ref} open={open} onClose={() => setOpen(false)} minWidth={280}>
        <div role="listbox" aria-label="Verteilung" style={{ padding: 4, display: 'flex', flexDirection: 'column' }}>
          {OPTS.map((o) => {
            const active = value === o.id;
            return (
              <button
                key={o.id}
                role="option"
                aria-selected={active}
                type="button"
                onClick={() => { onUpdate({ distribution: o.id }); setOpen(false); }}
                className={`text-left ${active ? 'bg-brand-soft text-brand font-semibold' : 'text-ink-2 hover:bg-surface-2'}`}
                style={{ border: 'none', padding: '8px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <DistIcon kind={o.id} size={16} />
                  <span className="flex-1">{o.label}</span>
                  <span className="text-ink-4" style={{ fontSize: 10 }}>{o.sub}</span>
                </div>
                {/* Entscheidungshilfe: wann + Beispiel, unter dem Label
                    eingerückt (auf Höhe des Icons). */}
                <div className="text-ink-3" style={{ fontSize: 10.5, marginTop: 3, marginLeft: 26, lineHeight: 1.35, fontWeight: 400 }}>
                  {o.when} · z.&nbsp;B. {o.ex}
                </div>
              </button>
            );
          })}
        </div>
        {/* Cpk außerhalb der listbox, damit dort nur Optionen stehen (ARIA). */}
        {current.id === 'normal' && <CpkFeld cpk={cpk} onUpdate={onUpdate} />}
      </PortalPopover>
    </>
  );
}
